/**
 * API-client. Praat met exact dezelfde backend-endpoints als de huidige
 * bundle, met exact dezelfde auth: een JWT onder localStorage-sleutel
 * `admin_access_token`, meegestuurd als `Authorization: Bearer …`.
 *
 * Het `<img>`-pad (live-screenshot) kan geen header zetten; daar hangt het
 * token als `?token=` achter de URL — net als in de huidige bundle.
 */

export const TOKEN_KEY = 'admin_access_token'
export const USER_KEY = 'admin_user'

export function getToken(): string {
  try {
    return localStorage.getItem(TOKEN_KEY) || ''
  } catch {
    return ''
  }
}

export function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token)
  } catch {
    /* private mode — de app werkt dan alleen deze sessie */
  }
}

export function clearAuth(): void {
  try {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    localStorage.removeItem(MEDIA_KEY)
  } catch {
    /* niets te doen */
  }
}

/**
 * Stil ingelogd blijven (03-10-2026, Jordy).
 *
 * Het inlogtoken geldt 24 uur. Wie bij het inloggen "Remember me" aanvinkte
 * (eigenaar, admin, manager) heeft daarnaast een refresh-cookie die geen
 * script kan lezen; daarmee haalt dit een nieuw inlogtoken zonder dat je je
 * wachtwoord opnieuw typt. Pas als dat niet lukt (geen cookie, 30 dagen niet
 * gebruikt, "Sign out everywhere", nieuw wachtwoord) volgt het inlogscherm.
 *
 * Eén verzoek tegelijk: tien panelen die tegelijk een 401 krijgen, delen één
 * vernieuwing.
 */
let vernieuwOnderweg: Promise<boolean> | null = null

export function vernieuwSessie(): Promise<boolean> {
  if (vernieuwOnderweg) return vernieuwOnderweg
  vernieuwOnderweg = (async () => {
    try {
      const res = await fetch('/api/admin/auth/refresh', { method: 'POST', credentials: 'same-origin' })
      if (!res.ok) return false
      const d = await res.json()
      if (!d?.access_token) return false
      setToken(d.access_token)
      try { if (d.user) localStorage.setItem(USER_KEY, JSON.stringify(d.user)) } catch { /* private mode */ }
      return true
    } catch {
      return false
    } finally {
      // Kort vasthouden, zodat late 401's van dezelfde golf het resultaat delen.
      window.setTimeout(() => { vernieuwOnderweg = null }, 2000)
    }
  })()
  return vernieuwOnderweg
}

/** Wanneer verloopt het huidige inlogtoken (ms sinds 1970), of null. */
export function tokenVerlooptOp(): number | null {
  const token = getToken()
  try {
    const deel = token.split('.')[1]
    if (!deel) return null
    const payload = JSON.parse(atob(deel.replace(/-/g, '+').replace(/_/g, '/')))
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

/** Uitloggen: de refresh-cookie wissen (kan alleen de server) en dan lokaal. */
export async function uitloggen(overal = false): Promise<void> {
  const token = getToken()
  // 18-10-2026 (Jordy, multi-chatter inbox): al mijn 5-minutenclaims en
  // open-labels vervallen meteen, zodat mijn gesprekken direct terug in de
  // wachtrij staan. Ook bij uitloggen wegens stilte (dat loopt hierlangs).
  if (token) {
    try {
      await fetch('/api/admin/conversations/panels/inbox/release', {
        method: 'POST', credentials: 'same-origin', headers: { Authorization: `Bearer ${token}` },
      })
    } catch { /* offline: de claims vervallen dan vanzelf na 5 minuten */ }
  }
  try {
    await fetch(overal ? '/api/admin/auth/logout-everywhere' : '/api/admin/auth/logout', {
      method: 'POST',
      credentials: 'same-origin',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
  } catch { /* offline: lokaal uitloggen gaat toch door */ }
  clearAuth()
}

/** Een fetch met het inlogtoken; bij een 401 één keer stil vernieuwen en opnieuw. */
async function metInlog(path: string, init: RequestInit, kop: Record<string, string>): Promise<Response> {
  const doe = () => {
    const token = getToken()
    return fetch(path, {
      ...init,
      credentials: 'same-origin',
      headers: { ...kop, ...(token ? { Authorization: `Bearer ${token}` } : {}), ...((init.headers as Record<string, string>) || {}) },
    })
  }
  let res = await doe()
  if (res.status === 401 && await vernieuwSessie()) res = await doe()
  if (res.status === 401) {
    // 03-10-2026: uitgelogd wegens stilte (chatter, laptop sliep of tabblad
    // dicht)? Dan zegt het inlogscherm dat, in plaats van niets.
    try {
      const d = await res.clone().json()
      if (String(d?.detail || '').includes('inactivity')) sessionStorage.setItem('novahub_uitlog_reden', 'inactief')
    } catch { /* geen JSON */ }
  }
  return res
}

export class ApiError extends Error {
  status: number
  /** 03-10-2026: het hele foutantwoord, voor wie meer nodig heeft dan
   *  `detail` (zoals de fouten per slot van `mass-message/schedule-batch`). */
  data: unknown
  constructor(status: number, message: string, data?: unknown) {
    super(message)
    this.status = status
    this.data = data
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await metInlog(path, init, { 'Content-Type': 'application/json' })
  if (res.status === 401) {
    clearAuth()
    throw new ApiError(401, 'Not logged in')
  }
  if (!res.ok) {
    let detail = `HTTP ${res.status}`
    let body: unknown
    try {
      body = await res.json()
      const b = body as { detail?: string; message?: string } | null
      detail = b?.detail || b?.message || detail
    } catch {
      /* geen JSON-body */
    }
    throw new ApiError(res.status, detail, body)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

/**
 * Gelijktijdige, identieke GET's delen één verzoek.
 *
 * 17-09-2026 (Jordy: "traag bij terugnavigeren"). Gemeten in een echte browser:
 * teruggaan van een chat naar het overzicht vuurde 26 verzoeken in drie
 * seconden af, waaronder vier keer `/conversations`, drie keer `auth/me` en
 * twee keer `models/personas` — verschillende panelen die tegelijk hetzelfde
 * vragen. Eén zo'n chatlijst kost bijna een seconde serverwerk, en er zijn maar
 * vier webprocessen voor de hele vloer.
 *
 * Vraagt iets binnen dit venster exact dezelfde URL, dan krijgt het de lopende
 * belofte in plaats van een nieuw verzoek. Het venster is kort genoeg om nooit
 * een poll te verslikken (die zitten seconden uit elkaar) en lang genoeg om de
 * stormloop bij het opbouwen van een pagina op te vangen.
 */
const VERSE_MS = 700
const lopend = new Map<string, { belofte: Promise<unknown>; ts: number }>()

function gedeeldeGet<T>(path: string): Promise<T> {
  const nu = Date.now()
  const bestaand = lopend.get(path)
  if (bestaand && nu - bestaand.ts < VERSE_MS) return bestaand.belofte as Promise<T>
  const belofte = request<T>(path).finally(() => {
    // Na afloop meteen vrijgeven: een volgende poll hoort weer echt te vragen.
    const huidig = lopend.get(path)
    if (huidig && huidig.belofte === belofte) lopend.delete(path)
  })
  lopend.set(path, { belofte, ts: nu })
  return belofte
}

export const api = {
  get: <T>(path: string) => gedeeldeGet<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body ?? {}) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /**
   * Een upload. Bewust NIET via `request`: die zet een JSON-content-type, en
   * dan laat de browser de multipart-grens weg en komt er aan de serverkant
   * een leeg formulier aan. Bij FormData moet de browser die header zelf
   * bepalen.
   */
  postForm: async <T>(path: string, form: FormData): Promise<T> => {
    const res = await metInlog(path, { method: 'POST', body: form }, {})
    if (res.status === 401) { clearAuth(); throw new ApiError(401, 'Not logged in') }
    if (!res.ok) {
      let detail = `HTTP ${res.status}`
      try {
        const body = await res.json()
        detail = body?.detail || body?.message || detail
      } catch { /* geen JSON-body */ }
      throw new ApiError(res.status, detail)
    }
    return (res.status === 204 ? undefined : await res.json()) as T
  },
}

/**
 * Een GET die ook de bladerheaders teruggeeft (`X-Next-Offset`, `X-Has-More`).
 * Bewust niet via `gedeeldeGet`: een bladzijde hoort precies één keer gevraagd
 * te worden, en de headers moeten bij dít antwoord horen.
 */
export async function getMetBladeren<T>(
  path: string,
): Promise<{ data: T; volgende: number | null; meer: boolean | null }> {
  const res = await metInlog(path, {}, { 'Content-Type': 'application/json' })
  if (res.status === 401) {
    clearAuth()
    throw new ApiError(401, 'Not logged in')
  }
  if (!res.ok) {
    let detail = `HTTP ${res.status}`
    try {
      const body = await res.json()
      detail = body?.detail || body?.message || detail
    } catch {
      /* geen JSON-body */
    }
    throw new ApiError(res.status, detail)
  }
  const kop = res.headers.get('X-Next-Offset')
  const meer = res.headers.get('X-Has-More')
  return {
    data: (await res.json()) as T,
    volgende: kop != null && kop !== '' && !Number.isNaN(Number(kop)) ? Number(kop) : null,
    meer: meer == null ? null : meer === '1',
  }
}

/** URL voor een live-browserframe; token in de query, zoals de bundle doet. */
export function liveShotUrl(which: string): string {
  const t = encodeURIComponent(getToken())
  return `/api/admin/posting/live-screenshot?which=${encodeURIComponent(
    which,
  )}&token=${t}&ts=${Date.now()}`
}

/**
 * De mediasleutel van vandaag (30-09-2026, Jordy).
 *
 * Achter een `<img>`/`<video>`-URL moet het token in de query, en dat was het
 * inlogtoken: goed voor élke API-aanroep, en zo belandde het in logs,
 * geschiedenis en gekopieerde links. Nu staat daar een aparte sleutel die
 * alleen voor media werkt, per kalenderdag (UTC) dezelfde is — dus stabiele
 * URL's en een werkende browsercache — en hooguit 48 uur geldig is. De server
 * kijkt bij elk verzoek of het account nog actief is.
 *
 * Bewaard in localStorage, gekoppeld aan het inlogtoken waarmee hij is
 * opgehaald. Op een nieuwe dag werkt de sleutel van gisteren nog, en wordt de
 * nieuwe op de achtergrond gehaald. Is er (nog) geen bruikbare sleutel, dan
 * valt de URL terug op het inlogtoken, zodat er nooit een leeg plaatje staat.
 */
export const MEDIA_KEY = 'novahub_mediasleutel'
/** Een sleutel die binnen deze tijd verloopt, gebruiken we niet meer. */
const MEDIA_MARGE_MS = 10 * 60_000
/** Na een mislukte ophaalpoging niet meteen opnieuw. */
const MEDIA_WACHT_MS = 5 * 60_000

type Mediasleutel = { token: string; dag: string; verloopt: string; voor: string }

let mediaOnderweg: Promise<void> | null = null
let mediaNietVoor = 0

function vandaagUtc(): string {
  return new Date().toISOString().slice(0, 10)
}

/** Herkenning van het inlogtoken, zonder het hele token nog eens op te slaan. */
function herkenning(inlog: string): string {
  return inlog.slice(-16)
}

function leesMediasleutel(): Mediasleutel | null {
  try {
    const ruw = localStorage.getItem(MEDIA_KEY)
    return ruw ? (JSON.parse(ruw) as Mediasleutel) : null
  } catch {
    return null
  }
}

/** Haal de sleutel van vandaag op; één verzoek tegelijk. */
export function haalMediaToken(): Promise<void> {
  if (mediaOnderweg) return mediaOnderweg
  const inlog = getToken()
  if (!inlog || Date.now() < mediaNietVoor) return Promise.resolve()
  mediaOnderweg = (async () => {
    try {
      const res = await fetch('/api/admin/auth/media-token', {
        credentials: 'same-origin',
        headers: { Authorization: `Bearer ${inlog}` },
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const d = await res.json()
      if (!d?.token) throw new Error('no key')
      const sleutel: Mediasleutel = {
        token: d.token, dag: d.dag, verloopt: d.verloopt, voor: herkenning(inlog),
      }
      localStorage.setItem(MEDIA_KEY, JSON.stringify(sleutel))
    } catch {
      mediaNietVoor = Date.now() + MEDIA_WACHT_MS
    } finally {
      mediaOnderweg = null
    }
  })()
  return mediaOnderweg
}

/** Het token voor achter een media-URL: de dagsleutel, anders het inlogtoken. */
export function mediaToken(): string {
  const inlog = getToken()
  if (!inlog) return ''
  const s = leesMediasleutel()
  const bruikbaar = !!s && s.voor === herkenning(inlog)
    && Date.parse(s.verloopt) - Date.now() > MEDIA_MARGE_MS
  if (!bruikbaar || s!.dag !== vandaagUtc()) void haalMediaToken()
  return bruikbaar ? s!.token : inlog
}

/**
 * De miniatuur van een content-item.
 *
 * Het token gaat mee in de query en niet in een header: een `<img>` kan geen
 * Authorization meesturen. Sinds 30-09 is dat de mediasleutel, zie `mediaToken`. De server maakt de miniatuur één keer aan en zet
 * hem in `data/thumbnails/`, dus de tweede keer komt hij uit die cache — ook
 * voor videoframes.
 */
export function thumbUrl(contentId: number): string {
  return `/api/admin/content/thumbnail/${contentId}?token=${encodeURIComponent(mediaToken())}`
}

/** Het bestand zelf, voor de vergrote weergave. */
export function fileUrl(contentId: number): string {
  return `/api/admin/content/file/${contentId}?token=${encodeURIComponent(mediaToken())}`
}

/**
 * De bijlage van een chatbericht.
 *
 * `messages.media_url` bevat twee soorten adressen. Een link van het platform
 * zelf (cdn.f2f.net en dergelijke) laten we ongemoeid: die is niet van ons en
 * verloopt vanzelf. Een adres dat naar onze eigen map wijst krijgt het token in
 * de query, want die route vraagt er sinds 09-09 om. Tot dan gaf hij bijlagen
 * uit gesprekken met fans vrij aan iedereen die het adres raadde.
 */
export function chatMediaUrl(url: string | null): string | null {
  if (!url) return url
  const scheiding = url.includes('?') ? '&' : '?'
  // 01-10-2026: de eigen kopie van een Mass DM. Een bibliotheekbestand vraagt
  // `token`, een eigen Mass DM-miniatuur heet het `auth_token` (de padnaam
  // bevat al een 'token'). Beide met de mediasleutel.
  if (url.startsWith('/api/admin/content/file/')) {
    return `${url}${scheiding}token=${encodeURIComponent(mediaToken())}`
  }
  if (url.startsWith('/api/admin/conversations/mass-message/media/')) {
    return `${url}${scheiding}auth_token=${encodeURIComponent(mediaToken())}`
  }
  if (!url.startsWith('/api/admin/conversations/media/')) return url
  return `${url}${scheiding}token=${encodeURIComponent(mediaToken())}`
}

/**
 * Een bestand ophalen en als download aanbieden.
 *
 * Waarom niet gewoon een link: een `<a href>` of een ingeplakte URL stuurt geen
 * Authorization-header mee. De server geeft dan 401 en de browser zegt alleen
 * "niet beschikbaar op deze site" — een foutmelding die niets uitlegt. Daarom
 * halen we het bestand op mét het token en bieden we het resultaat aan als
 * blob.
 */
export async function haalBestand(pad: string, naam: string): Promise<void> {
  const token = getToken()
  const res = await fetch(pad, {
    credentials: 'same-origin',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!res.ok) {
    let detail = `HTTP ${res.status}`
    try {
      const body = await res.json()
      detail = body?.detail || detail
    } catch { /* geen JSON */ }
    throw new ApiError(res.status, detail)
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = naam
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Even wachten met opruimen: sommige browsers lezen de blob pas ná de klik.
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000)
}
